// ============================================
// HELD ORDERS COMPONENT
// View and manage held/parked orders
// ============================================

import React from 'react';
import { 
  ArrowLeft, 
  Play, 
  Trash2, 
  Clock, 
  Package,
  Truck,
  UtensilsCrossed,
  User,
  Phone,
  MapPin,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const ORDER_TYPE_CONFIG = {
  dineIn: { label: 'Dine In', icon: UtensilsCrossed, color: 'bg-brand-orange', textColor: 'text-brand-orange' },
  takeaway: { label: 'Takeaway', icon: Package, color: 'bg-amber-500', textColor: 'text-amber-600' },
  delivery: { label: 'Delivery', icon: Truck, color: 'bg-blue-500', textColor: 'text-blue-600' },
};

// Format elapsed time
function formatElapsedTime(holdTime) {
  if (!holdTime) return '';
  const elapsed = Math.floor((Date.now() - new Date(holdTime).getTime()) / 60000);
  if (elapsed < 60) return `${elapsed} min ago`;
  const hours = Math.floor(elapsed / 60);
  const mins = elapsed % 60;
  return `${hours}h ${mins}m ago`;
}

export function HeldOrders({
  orders,
  onResumeOrder,
  onDeleteOrder,
  onBack,
  formatPrice,
  calculateTotals,
}) {
  return (
    <div data-testid="held-orders" className="flex-1 flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-3">
          <Button
            data-testid="btn-back-tables"
            variant="outline"
            size="icon"
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Held Orders</h2>
            <p className="text-sm text-gray-500">
              {orders.length} order{orders.length !== 1 ? 's' : ''} on hold
            </p>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {orders.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-2">No Held Orders</p>
              <p className="text-sm">Orders you hold will appear here</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map(order => {
                const typeConfig = ORDER_TYPE_CONFIG[order.type] || ORDER_TYPE_CONFIG.dineIn;
                const TypeIcon = typeConfig.icon;
                const totals = calculateTotals(order.items, order.type);

                return (
                  <Card 
                    key={order.id}
                    data-testid={`held-order-${order.id}`}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={cn('gap-1.5', typeConfig.color)}>
                            <TypeIcon className="w-3.5 h-3.5" />
                            {typeConfig.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {formatElapsedTime(order.holdTime)}
                        </div>
                      </div>
                      
                      {/* Customer Info */}
                      {order.customerName && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2 text-gray-700">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{order.customerName}</span>
                          </div>
                          {order.customerPhone && (
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />
                              {order.customerPhone}
                            </div>
                          )}
                          {order.deliveryAddress && (
                            <div className="flex items-start gap-2 text-sm text-gray-500 mt-1">
                              <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
                              <span className="line-clamp-2">{order.deliveryAddress}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardHeader>

                    <CardContent>
                      {/* Order Items */}
                      <div className="space-y-1 mb-4">
                        {order.items.slice(0, 4).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              {item.quantity}× {item.name}
                            </span>
                            <span className="font-mono text-gray-500">
                              {formatPrice(item.price * item.quantity)}
                            </span>
                          </div>
                        ))}
                        {order.items.length > 4 && (
                          <div className="text-xs text-gray-400">
                            +{order.items.length - 4} more items
                          </div>
                        )}
                      </div>

                      {/* Note */}
                      {order.note && (
                        <div className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg mb-4">
                          <MessageSquare className="w-4 h-4 text-amber-600 mt-0.5" />
                          <span className="text-sm text-amber-800">{order.note}</span>
                        </div>
                      )}

                      {/* Total */}
                      <div className="flex justify-between items-center py-2 border-t">
                        <span className="font-medium text-gray-700">Total</span>
                        <span className="font-mono font-bold text-lg">
                          {formatPrice(totals.total)}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-4">
                        <Button
                          data-testid={`btn-resume-${order.id}`}
                          className="flex-1 gap-2 bg-brand-orange hover:bg-brand-orange/90"
                          onClick={() => onResumeOrder(order)}
                        >
                          <Play className="w-4 h-4" />
                          Resume
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              data-testid={`btn-delete-${order.id}`}
                              variant="outline"
                              size="icon"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Held Order?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this held order. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => onDeleteOrder(order.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
